import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from 'app/store'
import { getBookingCodeListAPI } from 'utils/api'
import { viewModeType } from 'utils/constant/viewMode'
import { ErrorData, ErrorState } from 'features/error/errorSlice'

export interface BookingCodeBatchData {
  id: string
  name: string
  quantity: number
  used_code: number
  active_code: number
  expired_code: number
  valid_until: string
  created_at: string
  created_by: string
  status: string
}

export interface BookingCodeFilters {
  query: string
  created_at_from: string | null
  created_at_to: string | null
  valid_until_from: string | null
  valid_until_to: string | null
  status: string[]
}

export interface BookingCodeState {
  viewMode: string
  isLoading: boolean
  bookingCodes: BookingCodeBatchData[]
  total: number
  itemsPerPage: number
  currentPage: number
  filters: BookingCodeFilters
  errors: ErrorData
}

export interface BookingCodeListPayload {
  query?: string
  created_at_from?: number
  created_at_to?: number
  valid_until_from?: number
  valid_until_to?: number
  status?: string
  limit?: number
  offset?: number
}

export const initialState: BookingCodeState = {
  viewMode: viewModeType.IDLE,
  isLoading: false,
  bookingCodes: [],
  total: 0,
  itemsPerPage: 10,
  currentPage: 1,
  filters: {
    query: '',
    created_at_from: null,
    created_at_to: null,
    valid_until_from: null,
    valid_until_to: null,
    status: []
  },
  errors: null
}

export const getBookingCodeList = createAsyncThunk(
  'bookingCode/getBookingCodeList',
  async (payload: BookingCodeListPayload, { rejectWithValue }) => {
    try {
      return await getBookingCodeListAPI(payload)
    } catch (error) {
      const { status, data } = error.response
      if (data || status) return rejectWithValue({ data, status })
      return error
    }
  },
  {
    condition: (_, { getState }) => {
      const { error } = getState() as {
        error: ErrorState
      }
      if (
        error.status === 429 &&
        Math.floor(Date.now() / 1000) <= error.updateAt!!
      ) {
        return false
      }
    },
    dispatchConditionRejection: true
  }
)

export const bookingCodeSlice = createSlice({
  name: 'bookingCode',
  initialState: initialState,
  reducers: {
    setViewMode: (state, action) => {
      const { payload } = action
      state.viewMode = payload || initialState.viewMode
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setItemsPerPage: (state, action) => {
      state.itemsPerPage = action.payload
    },
    setFilterQuery: (state, action: PayloadAction<string>) => {
      state.filters = {
        ...initialState.filters,
        query: action.payload
      }
      state.currentPage = initialState.currentPage
    },
    setFilters: (state, action: PayloadAction<Partial<BookingCodeFilters>>) => {
      const { payload } = action
      if (!payload) return
      state.filters = { ...state.filters, ...payload }
      state.currentPage = initialState.currentPage
    },
    resetFilters: (
      state,
      action: PayloadAction<{
        createdAt: boolean
        validUntil: boolean
        statuses: boolean
      }>
    ) => {
      const { payload } = action
      if (!payload) {
        state.filters = initialState.filters
        return
      }
      const { createdAt, validUntil, statuses } = payload
      if (createdAt) {
        state.filters.created_at_from = initialState.filters.created_at_from
        state.filters.created_at_to = initialState.filters.created_at_to
      }
      if (validUntil) {
        state.filters.valid_until_from = initialState.filters.valid_until_from
        state.filters.valid_until_to = initialState.filters.valid_until_to
      }
      if (statuses) {
        state.filters.status = initialState.filters.status
      }
      state.currentPage = initialState.currentPage
    },
    resetPagination: (state) => {
      state.itemsPerPage = initialState.itemsPerPage
      state.currentPage = initialState.currentPage
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(getBookingCodeList.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getBookingCodeList.fulfilled, (state, action) => {
      state.isLoading = false
      state.errors = null
      const { total, booking_code_batches } = action.payload.data
      state.bookingCodes = booking_code_batches || initialState.bookingCodes
      state.total = total
    })
    builder.addCase(
      getBookingCodeList.rejected,
      (state, action: PayloadAction<any, string, any, any>) => {
        state.bookingCodes = initialState.bookingCodes
        state.total = initialState.total
        state.viewMode = initialState.viewMode
        state.isLoading = false
        state.errors = action.payload || {
          status: 500,
          data: `${action.error.name}: ${action.error.message}`
        }
      }
    )
  }
})

export const {
  setViewMode,
  setCurrentPage,
  setItemsPerPage,
  setFilterQuery,
  setFilters,
  resetFilters,
  resetPagination,
  resetState
} = bookingCodeSlice.actions

export const bookingCodeSelector = (state: RootState) =>
  state.bookingCode as BookingCodeState

export default bookingCodeSlice.reducer
